import { Dialog, DialogRef } from '@angular/cdk/dialog';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  HostBinding,
  inject,
  OnDestroy,
  signal,
  untracked,
  WritableSignal,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
  CollabEmptyStateCompactComponent,
  CollaborationDynamicTitlePipe,
  CollaborationEditorDialogComponent,
  CollaborationEditorDialogData,
  CollaborationEditorDialogResult,
  CollaborationListItemComponent,
  CollaborationScreenService,
} from '@libs/collaboration';
import { ScheduleDetailsComponent, SchedulesService } from '@libs/schedule';
import { DynamicRouterLinkDirective, TagEvent, TagPusherService } from '@libs/shared-services';
import { DropdownItem, TooltipModule } from '@libs/ui-lib';
import { DataState, routes } from '@libs/utils';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { TranslateModule } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { distinctUntilChanged, map } from 'rxjs';
import { Store } from '@ngrx/store';
import { createAppointmentActions, patientActions } from '@libs/store';
import { HealthHistoryDialogComponent, HealthHistoryDialogData } from '../health-history-dialog';
import { MY_COLLABORATION_SIDEBAR_APPOINTMENT, MY_COLLABORATION_SIDEBAR_VISIBLE } from './my-collaborations-sidebar-token';
import { AppointmentDetailsCollaborationsFilterService } from './appointment-my-collaborations.sidebar.service';
import { MyCollaborationsSidebarStore } from './my-collaborations-sidebar.store';

const COLLAB_SIDEBAR_LIMIT = 5;

@Component({
  selector: 'lib-my-collaborations-sidebar',
  templateUrl: './my-collaborations-sidebar.component.html',
  styleUrl: './my-collaborations-sidebar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    CollaborationListItemComponent,
    FormsModule,
    ReactiveFormsModule,
    SvgIconComponent,
    TranslateModule,
    ScheduleDetailsComponent,
    CollaborationDynamicTitlePipe,
    DynamicRouterLinkDirective,
    TooltipModule,
    CollabEmptyStateCompactComponent,
  ],
  providers: [MyCollaborationsSidebarStore],
})
export class MyCollaborationsSidebarComponent implements OnDestroy {
  private readonly collabScreenService = inject(CollaborationScreenService);
  private readonly schedulesService = inject(SchedulesService);
  private readonly collaborationFilterStore = inject(MyCollaborationsSidebarStore);
  private readonly appointmentDetailsCollaborationFilterService = inject(AppointmentDetailsCollaborationsFilterService);
  private readonly dialog = inject(Dialog);
  private readonly activatedRoute = inject(ActivatedRoute);
  protected readonly scheduleAppointment = inject(MY_COLLABORATION_SIDEBAR_APPOINTMENT);
  protected readonly isSidebarVisible = inject(MY_COLLABORATION_SIDEBAR_VISIBLE);
  private readonly destroyRef = inject(DestroyRef);
  private readonly store = inject(Store);
  private readonly tagPusherService = inject(TagPusherService);

  protected readonly routes = routes;
  protected readonly focusedCollabId: WritableSignal<string | null> = signal(null);
  protected readonly collabs = computed(
    () => this.collabScreenService.viewModel().collaborations.data?.slice(0, COLLAB_SIDEBAR_LIMIT) ?? [],
  );
  protected readonly currentUserIds = computed(() => this.collabScreenService.viewModel().currentUserIds);
  protected readonly sites = computed(() => this.collabScreenService.viewModel().sites);
  protected readonly siteOptions = computed<DropdownItem<string>[]>(() => this.sites().map(({ id, name }) => ({ value: id, label: name })));
  protected readonly selectedSiteId = computed(() => this.collabScreenService.viewModel().selectedSite?.id);
  protected collaborationFiltersState = computed(() => {
    return {
      myCollabs: {
        directOnly: this.collaborationFilterStore.filterDirectOnly(),
        unreadOnly: this.collaborationFilterStore.filterUnreadOnly(),
        teamCollab: this.collaborationFilterStore.filterTeamCollab(),
        clinicalCollab: this.collaborationFilterStore.filterClinicalCollab(),
      },
      appointmentCollabs: {
        directOnly: this.appointmentDetailsCollaborationFilterService.state().filterDirectOnly,
        unreadOnly: this.appointmentDetailsCollaborationFilterService.state().filterUnreadOnly,
      },
    };
  });
  protected isDirectToMeActive = computed(() => {
    return this.scheduleAppointment()
      ? this.collaborationFiltersState().appointmentCollabs.directOnly
      : this.collaborationFiltersState().myCollabs.directOnly;
  });
  protected isUnreadActive = computed(() => {
    return this.scheduleAppointment()
      ? this.collaborationFiltersState().appointmentCollabs.unreadOnly
      : this.collaborationFiltersState().myCollabs.unreadOnly;
  });
  protected isTeamCollabActive = computed(() => this.collaborationFiltersState().myCollabs.teamCollab);
  protected isClinicalCollabActive = computed(() => this.collaborationFiltersState().myCollabs.clinicalCollab);
  protected readonly isCollabListLoading = computed(() => this.collabScreenService.viewModel().collaborations.state === DataState.Loading);
  protected readonly showMessageCount = computed(() => this.collabScreenService.viewModel().collaborations.state !== DataState.Initial);
  protected readonly showEmptyState = computed(
    () => this.collabScreenService.viewModel().collaborations.state === DataState.Fetched && this.collabs().length === 0,
  );
  protected readonly isFiltered = computed(() => {
    return this.scheduleAppointment()
      ? this.collaborationFiltersState().appointmentCollabs.directOnly || this.collaborationFiltersState().appointmentCollabs.unreadOnly
      : this.collaborationFiltersState().myCollabs.directOnly || this.collaborationFiltersState().myCollabs.unreadOnly;
  });

  /**
   * The onboarding empty state replaces the filters, results and new collab button in the "My Collabs" sidebar when the user has no collabs
   * at all. A filtered-to-zero list keeps the regular chrome so the filters stay reachable, and the appointment context keeps its own
   * schedule details.
   */
  protected readonly showFullEmptyState = computed(() => !this.scheduleAppointment() && this.showEmptyState() && !this.isFiltered());

  /**
   * The surrounding sidebar container is a definite-height box, but the host itself is only max-h-full. The onboarding empty state needs a
   * definite height so its content can centre vertically and the support link can sit on the bottom edge.
   */
  @HostBinding('class.h-full') public get isFullHeight(): boolean {
    return this.showFullEmptyState();
  }
  protected readonly showSeeAll = computed(
    () =>
      (this.collabScreenService.viewModel().collaborations.state === DataState.Fetched &&
        (this.collabs().length > 0 || this.isFiltered())) ||
      (this.isCollabListLoading() && this.collabs().length > 0),
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private dialogRef: DialogRef<any> | null = null;

  constructor() {
    effect(() => {
      const appointment = this.scheduleAppointment();

      untracked(() => {
        if (appointment) {
          this.collabScreenService.init(appointment?.patientId, false, {
            directOnly: this.collaborationFiltersState().appointmentCollabs.directOnly,
            unreadOnly: this.collaborationFiltersState().appointmentCollabs.unreadOnly,
          });
        } else {
          this.collabScreenService.init(undefined, false, {
            directOnly: this.collaborationFiltersState().myCollabs.directOnly,
            unreadOnly: this.collaborationFiltersState().myCollabs.unreadOnly,
          });
        }
      });
    });

    this.activatedRoute.params
      .pipe(
        map((params) => params['siteId']),
        takeUntilDestroyed(this.destroyRef),
        distinctUntilChanged(),
      )
      .subscribe(() => {
        this.collabScreenService.init(undefined, false, {
          directOnly: this.collaborationFiltersState().myCollabs.directOnly,
          unreadOnly: this.collaborationFiltersState().myCollabs.unreadOnly,
        });
      });
  }

  public ngOnDestroy(): void {
    this.dialogRef?.close();
    this.collabScreenService.destroy();
  }

  protected onCreateNewCollab(): void {
    this.tagPusherService.pushTag({ event: TagEvent.CollaborationStarted });

    this.dialogRef = this.dialog.open<CollaborationEditorDialogResult, CollaborationEditorDialogData>(CollaborationEditorDialogComponent, {
      backdropClass: ['backdrop-blur-overlay', 'bg-overlay-light'],
      panelClass: ['w-1/2'],
      disableClose: true,
      data: {
        siteId: this.selectedSiteId(),
        sites: this.siteOptions(),
        currentUserIds: this.currentUserIds(),
        showSiteSelector: this.selectedSiteId() !== routes.showcaseSiteId,
      },
    });

    this.dialogRef.closed.subscribe((result) => {
      if (result) {
        this.tagPusherService.pushTag({
          event: TagEvent.CollaborationCreated,
          patient_linked: result.linkedPatient ? 1 : 0,
          patient_invited: result.patients.length > 0 ? 1 : 0,
          participant_added: result.employees.length,
        });
        this.collabScreenService.createCollaboration({
          ...result,
          navigateToCollaboration: true,
        });
      }
    });
  }

  protected setFocusedCollab(collabId: string): void {
    this.focusedCollabId.set(collabId);
  }

  protected removeFocusedCollab(): void {
    this.focusedCollabId.set(null);
  }

  protected onCloseClick(): void {
    this.store.dispatch(createAppointmentActions.onResetCreateAppointment());
    this.schedulesService.setAppointmentId(null);
  }

  protected onScheduleAvatarClick(): void {
    this.store.dispatch(patientActions.onOpenHealthHistory({ patientId: this.scheduleAppointment()?.patientId ?? '' }));
    this.dialogRef = this.dialog.open<HealthHistoryDialogComponent, HealthHistoryDialogData>(HealthHistoryDialogComponent, {
      backdropClass: ['backdrop-blur-overlay', 'bg-overlay-light'],
      panelClass: ['w-[1356px]', 'h-full', 'pt-[84px]', 'pb-[12px]'],
      data: {
        siteId: this.selectedSiteId(),
        patientId: this.scheduleAppointment()?.patientId,
      },
    });
  }

  public onScheduleEditClick(): void {
    this.store.dispatch(createAppointmentActions.onSetIsEditAppointment({ isShowcase: this.selectedSiteId() === routes.showcaseSiteId }));
    this.schedulesService.editSelectedAppointment({ isEdited: true });
  }

  public onDirectToMeClick(): void {
    if (this.scheduleAppointment()) {
      this.appointmentDetailsCollaborationFilterService.updateFilters({
        directOnly: !this.appointmentDetailsCollaborationFilterService.state().filterDirectOnly,
        unreadOnly: this.appointmentDetailsCollaborationFilterService.state().filterUnreadOnly,
      });
    } else {
      this.collaborationFilterStore.updateFilters({
        directOnly: !this.collaborationFilterStore.filterDirectOnly(),
        unreadOnly: this.collaborationFilterStore.filterUnreadOnly(),
        teamCollab: this.collaborationFilterStore.filterTeamCollab(),
        clinicalCollab: this.collaborationFilterStore.filterClinicalCollab(),
      });
    }
  }

  public onUnreadClick(): void {
    if (this.scheduleAppointment()) {
      this.appointmentDetailsCollaborationFilterService.updateFilters({
        directOnly: this.appointmentDetailsCollaborationFilterService.state().filterDirectOnly,
        unreadOnly: !this.appointmentDetailsCollaborationFilterService.state().filterUnreadOnly,
      });
    } else {
      this.collaborationFilterStore.updateFilters({
        directOnly: this.collaborationFilterStore.filterDirectOnly(),
        unreadOnly: !this.collaborationFilterStore.filterUnreadOnly(),
        teamCollab: this.collaborationFilterStore.filterTeamCollab(),
        clinicalCollab: this.collaborationFilterStore.filterClinicalCollab(),
      });
    }
  }

  public onTeamCollabClick(): void {
    this.collaborationFilterStore.updateFilters({
      directOnly: this.collaborationFilterStore.filterDirectOnly(),
      unreadOnly: this.collaborationFilterStore.filterUnreadOnly(),
      teamCollab: !this.collaborationFilterStore.filterTeamCollab(),
      clinicalCollab: this.collaborationFilterStore.filterClinicalCollab(),
    });
  }

  public onClinicalCollabClick(): void {
    this.collaborationFilterStore.updateFilters({
      directOnly: this.collaborationFilterStore.filterDirectOnly(),
      unreadOnly: this.collaborationFilterStore.filterUnreadOnly(),
      teamCollab: this.collaborationFilterStore.filterTeamCollab(),
      clinicalCollab: !this.collaborationFilterStore.filterClinicalCollab(),
    });
  }

  protected onLeaveCollaborationClick(collabId: string): void {
    this.collabScreenService.leaveCollaboration({ collabId });
  }

  protected onRecoverCollaborationClick(collabId: string): void {
    this.collabScreenService.recoverCollaboration({ collabId });
  }

  protected onDeleteCollaborationClick(collabId: string, _isPermanent: boolean): void {
    this.collabScreenService.deleteCollaboration({ collabId });
  }
}
